import { useState } from 'react'

import { Box, Hint } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { F0Callout } from '@factorialco/f0-react/dist/experimental'
import { Settings } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import type { LeaveApprovalRecommendationFragment } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import app from 'modules/core/store/models/app'

import { RECOMMENDATION_ACTION, RecommendationAction } from './RecommendationAction'
import sdm from './assets/sdm.jpg'
import {
  RecommendationReason,
  getRecommendationDescriptionRich,
} from './getRecommendationDescriptionRich'

interface Props {
  employeeName: string
  employeeId: number
  recommendation: LeaveApprovalRecommendationFragment | null | undefined
  workable?: boolean
}

const LeaveApprovalRecommendation = ({
  employeeName,
  employeeId,
  recommendation,
  workable,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const storageKey = `${LOCAL_STORAGE_KEYS.leaveApprovalRecommendationModal}:${employeeId}`
  const [hasSeenModal, setHasSeenModal] = useLocalStorage<boolean>(storageKey, false)

  const handleModalClose = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    track(
      trackingEvents.timeoff.leaveApprovalRecommendation.personalizedRuleModal.imInterestedClicked,
      {
        employee_name: employeeName,
        employee_id: employeeId,
        resource_type: 'timeoff',
      }
    )
    setHasSeenModal(true)
    setIsModalOpen(false)
    app.setFlash({
      type: 'correct',
      message: i18n.t('time_off.approval_recommendation.modal_success_message'),
    })
  }

  const handleModalOpen = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    track(trackingEvents.timeoff.leaveApprovalRecommendation.personalizedRuleModal.modalOpened, {
      employee_name: employeeName,
      employee_id: employeeId,
      resource_type: 'timeoff',
    })
    setIsModalOpen(true)
  }

  const onFinish = () => {
    if (!hasSeenModal) {
      setIsModalOpen(true)
    }
  }

  const getRecommendationText = (action: RecommendationAction) => {
    switch (action) {
      case RECOMMENDATION_ACTION.APPROVE:
        return i18n.t('time_off.approval_recommendation.approval_suggested')
      case RECOMMENDATION_ACTION.REVIEW:
        return i18n.t('time_off.approval_recommendation.review_needed')
      case RECOMMENDATION_ACTION.NO_RECOMMENDATION:
        return i18n.t('time_off.approval_recommendation.not_enough_information')
      default:
        return ''
    }
  }
  const getRecommendationType = (action: RecommendationAction) => {
    switch (action) {
      case RECOMMENDATION_ACTION.APPROVE:
        return 'positive'
      case RECOMMENDATION_ACTION.REVIEW:
        return 'warning'
      case RECOMMENDATION_ACTION.NO_RECOMMENDATION:
        return 'info'
      default:
        return 'info'
    }
  }

  const recommendedAction = recommendation?.action
  const recommendationAction: RecommendationAction | '' =
    recommendedAction === RECOMMENDATION_ACTION.APPROVE ||
    recommendedAction === RECOMMENDATION_ACTION.REVIEW ||
    recommendedAction === RECOMMENDATION_ACTION.NO_RECOMMENDATION
      ? recommendedAction
      : ''
  const recommendedReason = recommendation?.reason

  const recommendationReason: RecommendationReason | '' =
    recommendedReason && typeof recommendedReason === 'object' ? recommendedReason : ''

  if (!recommendationAction) return null

  const recommendationText = getRecommendationText(recommendationAction)
  const recommendationType = getRecommendationType(recommendationAction)
  const recommendationDescription = getRecommendationDescriptionRich(
    recommendationAction,
    recommendationReason,
    employeeName,
    workable
  )
  const buildModalDescription = () => {
    return (
      <div style={{ textAlign: 'center' }}>
        {i18n.t('time_off.approval_recommendation.modal_description')}
      </div>
    )
  }

  return (
    <>
      <F0Callout
        title={recommendationText}
        variant={recommendationType}
        actions={
          !hasSeenModal
            ? [
                {
                  label: `${i18n.t('time_off.approval_recommendation.callout_button')}`,
                  onClick: handleModalOpen,
                  icon: Settings,
                },
              ]
            : []
        }
      >
        {recommendationDescription}
      </F0Callout>

      {isModalOpen && (
        <Hint.NewModal onClose={() => setIsModalOpen(false)} onFinish={onFinish} maxWidth="s12">
          <Hint.NewModal.ImageStep src={sdm} title={''} description={buildModalDescription()}>
            <Box textAlign="center" alignItems="center" justifyContent="center">
              <F0Button
                label={i18n.t('time_off.approval_recommendation.modal_button')}
                onClick={handleModalClose}
                size="md"
                variant="default"
                weight="medium"
                type="button"
              />
            </Box>
          </Hint.NewModal.ImageStep>
        </Hint.NewModal>
      )}
    </>
  )
}

export default LeaveApprovalRecommendation
